import axios, { AxiosInstance } from 'axios';
import {
  DEFAULT_HEADERS_BY_SERVICE,
  DEFAULT_TIMEOUTS,
  HTTP_METHODS
} from './api.constants';
import {
  axiosRejectedResponseInterceptor,
  axiosSuccessResponseInterceptor
} from './api.error.helpers';
import { setHeaders } from './api.headers';
import { ApiInstanceConfiguration } from './api.interfaces';
import { updateAuthorizationHeader } from './axios.interceptors';
import { Microservice } from './microservice.interfaces';
import { queryClient } from './reactQuery/ReactQueryClientProvider';
import {
  CodeTableNumber,
  MetadataCodeWithAdditionalPropertiesDictionary
} from '../interfaces/codeTables/codeTables';
import { codeTableMetadataQueryKey } from './reactQuery/codeTables/useCodeTableMetadata';

export const MircoserviceValue: Record<Microservice, string> = {
  appointment: '25',
  'arc-config': '17',
  'customer-notify': '21',
  datalake: '7',
  documentation: '14',
  'document-output': '18',
  generalledger: '4',
  remarks: '1',
  reporting: '9',
  taxation: '15',
  'file-management': '27',
  serviceability: '28'
};

let config: ApiInstanceConfiguration;

const microserviceInstances: Partial<Record<Microservice, AxiosInstance>> = {};
/**
 * Initialize API for a microservices endpoint.
 */
export function initMicroservice(
  microservice: Microservice,
  returnFullResponse?: boolean
) {
  if (!config) {
    throw new Error(
      'Cannot initialize microservice prior to getting configuration'
    );
  }
  if (!microserviceInstances[microservice]) {
    microserviceInstances[microservice] = axios.create({
      baseURL: buildMicroserviceUrl(microservice, config.environment.value),
      headers: {
        common: setHeaders(DEFAULT_HEADERS_BY_SERVICE.microservices, config)
      },
      method: HTTP_METHODS.POST,
      responseType: 'json',
      timeout: DEFAULT_TIMEOUTS.MICROSERVICES
    });

    microserviceInstances[microservice]?.interceptors.request.use(
      updateAuthorizationHeader()
    );

    microserviceInstances[microservice]?.interceptors.response.use(
      (response) => {
        /**
         * this is a temporary fix for axios. there is a problem in axios code which does not pass
         * responseType to XMLHttpRequest. check adapter/xhr.js, line where request.responseType is assigned.
         */
        if (typeof response.data === 'string') {
          try {
            response.data = JSON.parse(response.data);
            // eslint-disable-next-line no-empty
          } catch (_e) {}
        }
        return axiosSuccessResponseInterceptor(response, returnFullResponse);
      },
      axiosRejectedResponseInterceptor
    );
  }
  return microserviceInstances[microservice];
}

export function getAxiosMicroserviceInstance(
  microservice: Microservice,
  returnFullResponse?: boolean
): AxiosInstance {
  if (!microserviceInstances[microservice]) {
    initMicroservice(microservice, returnFullResponse);
  }
  return microserviceInstances[microservice]!;
}

export function getInstanceIdentifier(microservice: Microservice) {
  const microServiceConfigData = queryClient.getQueriesData({
    queryKey: [
      codeTableMetadataQueryKey,
      CodeTableNumber.MicroserviceConfiguration
    ]
  });

  if (microServiceConfigData?.length > 0) {
    const microServiceConfigs =
      microServiceConfigData[0][1] as MetadataCodeWithAdditionalPropertiesDictionary<CodeTableNumber.MicroserviceConfiguration>[];
    if (microServiceConfigs?.length > 0) {
      const config =
        microServiceConfigs?.find(
          (config) =>
            config.AdditionalProperties?.microservice_type_code ===
              MircoserviceValue[microservice] &&
            !config.Global &&
            config.AdditionalProperties?.is_active === 'True'
        ) ||
        microServiceConfigs?.find(
          (config) =>
            config.AdditionalProperties?.microservice_type_code ===
              MircoserviceValue[microservice] && config.Global
        );
      return (
        (config?.AdditionalProperties?.is_dedicated_instance === 'True' &&
          config?.AdditionalProperties?.instance_identifier) ||
        undefined
      );
    }
  }
  return undefined;
}

function buildMicroserviceUrl(microservice: Microservice, environment: string) {
  const instanceIdentifier = getInstanceIdentifier(microservice);
  let url = '';
  if (
    environment &&
    (microservice === 'documentation' ||
      microservice === 'datalake' ||
      microservice === 'reporting' ||
      microservice === 'customer-notify' ||
      microservice === 'appointment' ||
      microservice === 'file-management' ||
      microservice === 'serviceability')
  ) {
    url = instanceIdentifier
      ? `https://${microservice}.${environment}-${instanceIdentifier}.ascendon.tv`
      : `https://${microservice}.${environment}.ascendon.tv`;
  } else if (environment && microservice === 'arc-config') {
    url = instanceIdentifier
      ? `https://${microservice}-${environment}-${instanceIdentifier}.${environment}.${instanceIdentifier}.ascendon.tv`
      : `https://${microservice}.${environment}.msvc-${environment}.ascendon.tv`;
  } else if (environment) {
    url = instanceIdentifier
      ? `https://${microservice}.${environment}-${instanceIdentifier}.${environment}.${instanceIdentifier}.ascendon.tv`
      : `https://${microservice}.${environment}.msvc-${environment}.ascendon.tv`;
  }
  return url;
}

export function setEnvironment(apiConfig: ApiInstanceConfiguration) {
  config = apiConfig;
}
